/**
 * System Parameter Center hooks (Issue #267).
 *
 * Wraps the typed `@jinzhuan/api-client` calls so the `/settings` system
 * parameter center can read the catalog, create change requests, approve /
 * reject pending changes, and roll back published versions.
 *
 * 红线：
 * - 所有数据通过 `@jinzhuan/api-client` typed client 调用，禁止手写 fetch / 假
 *   成功状态。
 * - 写入必须填写 reason；高风险变更额外要求 approvalNo + 服务端可验证的
 *   `secondFactorChallengeId`（由 face_verification_challenges scene=
 *   high_risk_action 的成功挑战签发），前端只传 challenge id，不传任何裸
 *   `secondFactorResult=passed` 字符串。校验由后端权威执行；前端只把表单值原样
 *   传给后端，并把 4xx/5xx 错误展示给操作人，不在前端伪装成功或自动补默认值。
 * - 写入成功后必须重新拉取后端列表，不依赖前端乐观状态。
 */
import { useCallback, useEffect, useState } from 'react';
import {
  createApiClient,
  type ApproveSystemParameterChangeRequest,
  type CreateSystemParameterChangeRequest,
  type RejectSystemParameterChangeRequest,
  type RollbackSystemParameterChangeRequest,
  type SystemParameterChangeDto,
  type SystemParameterDto,
  type SystemParameterValueDto,
  type SystemParameterVersionDto,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export interface AsyncState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function pickErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const maybeMessage = (err as { message?: string }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return fallback;
}

export interface SystemParameterCatalogReturn {
  readonly catalog: AsyncState<readonly SystemParameterDto[]>;
  readonly changes: AsyncState<readonly SystemParameterChangeDto[]>;
  refresh: () => Promise<void>;
  createChange: (input: CreateSystemParameterChangeRequest) => Promise<SystemParameterChangeDto>;
  approveChange: (
    id: string,
    input: ApproveSystemParameterChangeRequest,
  ) => Promise<SystemParameterChangeDto>;
  rejectChange: (
    id: string,
    input: RejectSystemParameterChangeRequest,
  ) => Promise<SystemParameterChangeDto>;
  rollback: (
    versionId: string,
    input: RollbackSystemParameterChangeRequest,
  ) => Promise<SystemParameterChangeDto>;
  listVersions: (parameterId: string) => Promise<readonly SystemParameterVersionDto[]>;
}

/**
 * Loads the catalog + pending change list in parallel. Both slices keep
 * independent loading/error so a partial failure does not blank the section.
 */
export function useSystemParameterCenter(): SystemParameterCatalogReturn {
  const [catalog, setCatalog] = useState<AsyncState<readonly SystemParameterDto[]>>({
    data: null,
    loading: false,
    error: null,
  });
  const [changes, setChanges] = useState<AsyncState<readonly SystemParameterChangeDto[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const loadCatalog = useCallback(async () => {
    setCatalog((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/settings/system-parameters/catalog',
      );
      if (error) {
        setCatalog({ data: null, loading: false, error: pickErrorMessage(error, '加载系统参数目录失败') });
        return;
      }
      setCatalog({ data: data ?? [], loading: false, error: null });
    } catch (err) {
      setCatalog({ data: null, loading: false, error: pickErrorMessage(err, '加载系统参数目录失败') });
    }
  }, []);

  const loadChanges = useCallback(async () => {
    setChanges((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/settings/system-parameters/changes',
        { params: { query: { limit: 50 } } },
      );
      if (error) {
        setChanges({ data: null, loading: false, error: pickErrorMessage(error, '加载变更申请失败') });
        return;
      }
      setChanges({ data: data?.rows ?? [], loading: false, error: null });
    } catch (err) {
      setChanges({ data: null, loading: false, error: pickErrorMessage(err, '加载变更申请失败') });
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([loadCatalog(), loadChanges()]);
  }, [loadCatalog, loadChanges]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createChange = useCallback(
    async (input: CreateSystemParameterChangeRequest) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/settings/system-parameters/changes',
        { body: input },
      );
      if (error) {
        throw new Error(pickErrorMessage(error, '创建变更申请失败'));
      }
      if (!data) {
        throw new Error('服务端未返回变更申请实体');
      }
      await refresh();
      return data;
    },
    [refresh],
  );

  const approveChange = useCallback(
    async (id: string, input: ApproveSystemParameterChangeRequest) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/settings/system-parameters/changes/{id}/approve',
        { params: { path: { id } }, body: input },
      );
      if (error) {
        throw new Error(pickErrorMessage(error, '审批变更失败'));
      }
      if (!data) {
        throw new Error('服务端未返回审批结果');
      }
      await refresh();
      return data;
    },
    [refresh],
  );

  const rejectChange = useCallback(
    async (id: string, input: RejectSystemParameterChangeRequest) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/settings/system-parameters/changes/{id}/reject',
        { params: { path: { id } }, body: input },
      );
      if (error) {
        throw new Error(pickErrorMessage(error, '拒绝变更失败'));
      }
      if (!data) {
        throw new Error('服务端未返回拒绝结果');
      }
      await refresh();
      return data;
    },
    [refresh],
  );

  const rollback = useCallback(
    async (versionId: string, input: RollbackSystemParameterChangeRequest) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/settings/system-parameters/versions/{versionId}/rollback',
        { params: { path: { versionId } }, body: input },
      );
      if (error) {
        throw new Error(pickErrorMessage(error, '回滚参数失败'));
      }
      if (!data) {
        throw new Error('服务端未返回回滚结果');
      }
      await refresh();
      return data;
    },
    [refresh],
  );

  const listVersions = useCallback(async (parameterId: string) => {
    const { data, error } = await apiClient.GET(
      '/api/v1/settings/system-parameters/{parameterId}/versions',
      { params: { path: { parameterId } } },
    );
    if (error) {
      throw new Error(pickErrorMessage(error, '加载版本历史失败'));
    }
    return data ?? [];
  }, []);

  return {
    catalog,
    changes,
    refresh,
    createChange,
    approveChange,
    rejectChange,
    rollback,
    listVersions,
  };
}

export type {
  SystemParameterDto,
  SystemParameterValueDto,
  SystemParameterChangeDto,
  SystemParameterVersionDto,
  CreateSystemParameterChangeRequest,
  ApproveSystemParameterChangeRequest,
  RejectSystemParameterChangeRequest,
  RollbackSystemParameterChangeRequest,
};
